// pages/ProductsPage.tsx
import { useProducts, useAllProducts } from '@/hooks/useProducts';
import { useCategoryBySlug } from '@/hooks/useCategories';
import { ProductSortBy, ProductType, ProductFilters, Product } from '@bookstore/shared';
import {
  Grid,
  List,
  Loader2,
  BookOpen,
  Package,
  Sparkles,
  X,
  SlidersHorizontal,
  Check,
} from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import ProductCard from '@/components/common/ProductCard';
import CategoryTreeFilter from '@/components/common/CategoryTreeFilter';
import Pagination from '@/components/common/Pagination';
import ProductCardHorizontal from '@/components/common/ProductsCardHorizontal';

const PRIMARY = '#A8523A';
const PRIMARY_DARK = '#8F4430';
const PRIMARY_TINT = 'rgba(168,82,58,0.125)';
const IVORY = '#F8F5F0';
const MUTED = '#F5F2ED';
const TEXT = '#2D2D2D';
const BORDER = 'rgba(45,45,45,0.1)';

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { categorySlug } = useParams();

  // sorts / view
  const [sortBy, setSortBy] = useState<ProductSortBy>('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

  // Pagination mode: 'infinite' or 'pages'
  const [paginationMode, setPaginationMode] = useState<'infinite' | 'pages'>('pages');
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = 20;

  // page-level category
  const { data: category } = useCategoryBySlug(categorySlug);

  // // all categories
  // const { data: allCategories } = useCategories();

  // filter options state - initialize from URL params
  const [filterOptions, setFilterOptions] = useState<ProductFilters>(() => {
    const inStockParam = searchParams.get('inStock');
    return {
      minPrice: 0,
      maxPrice: 1000,
      inStock: inStockParam === 'true' ? true : undefined,
      tags: [],
    };
  });

  // mobile filter modal state
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // determine productType from current category or query
  const productTypeFilter = useMemo(() => {
    if ((category as any)?.productType) return (category as any).productType as ProductType;
    const typeParam = searchParams.get('type');
    if (typeParam === 'book') return ProductType.BOOK;
    if (typeParam === 'stationary') return ProductType.STATIONERY;
    return undefined;
  }, [category, searchParams]);

  // Get search query from URL
  const searchQuery = searchParams.get('search') || '';

  // Use client-side search if there's a search query, otherwise use regular products query
  const { data: allProductsData, isLoading: isLoadingAllProducts } = useAllProducts();

  // combine filters for API call (only when NOT searching)
  const apiFilters: ProductFilters = useMemo(
    () => ({
      ...filterOptions,
      categoryId: category?.id, // Use category from URL route
      productType: productTypeFilter,
      isFeatured: searchParams.get('featured') === 'true' ? true : undefined,
    }),
    [filterOptions, category, productTypeFilter, searchParams]
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingProducts,
    refetch,
  } = useProducts(apiFilters, sortBy, pageSize);

  // Client-side filtering for search
  const searchFilteredProducts = useMemo(() => {
    if (!searchQuery || !allProductsData) return [];

    const query = searchQuery.toLowerCase().trim();
    const queryWords = query.split(/\s+/).filter(word => word.length > 0);

    let filtered = allProductsData.filter((product: Product) => {
      const searchText = [
        product.title,
        product.description || '',
        product.author || '',
        product.brand || '',
        product.isbn || '',
      ].join(' ').toLowerCase();

      return queryWords.some(word => searchText.includes(word));
    });

    // Apply additional filters
    if (filterOptions.inStock) {
      filtered = filtered.filter((p: Product) => p.stock > 0);
    }
    if (productTypeFilter) {
      filtered = filtered.filter((p: Product) => p.productType === productTypeFilter);
    }
    if (category?.id) {
      filtered = filtered.filter((p: Product) => 
        p.categories?.includes(category.id)
      );
    }
    if (filterOptions.minPrice !== undefined) {
      filtered = filtered.filter((p: Product) => p.price >= filterOptions.minPrice!);
    }
    if (filterOptions.maxPrice !== undefined) {
      filtered = filtered.filter((p: Product) => p.price <= filterOptions.maxPrice!);
    }

    // Client-side sorting
    const sorted = [...filtered];
    switch (sortBy) {
      case 'price-asc':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        sorted.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'popular':
        sorted.sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
        break;
      case 'newest':
      default:
        sorted.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });
        break;
    }

    return sorted;
  }, [searchQuery, allProductsData, filterOptions, productTypeFilter, category, sortBy]);

  const isLoading = searchQuery ? isLoadingAllProducts : isLoadingProducts;

const allProducts = searchQuery 
  ? searchFilteredProducts
  : data?.pages.flatMap((page) =>
      page && typeof page === 'object' && 'products' in page ? (page as any).products : []
    ) || [];

// Get total count from first API response
const totalCount = data?.pages[0]?.totalCount;
const totalLoadedProducts = allProducts.length;

// Calculate total pages
const calculatedTotalPages = totalCount 
  ? Math.ceil(totalCount / pageSize)
  : Math.ceil(totalLoadedProducts / pageSize);

const displayTotalPages = totalCount 
  ? calculatedTotalPages
  : (hasNextPage ? calculatedTotalPages + 1 : calculatedTotalPages);

const startIdx = (currentPage - 1) * pageSize;
const endIdx = startIdx + pageSize;
const currentPageProducts = allProducts.slice(startIdx, endIdx);



  // Show products based on mode
  const products = paginationMode === 'pages' ? currentPageProducts : allProducts;

  // reset scroll and page on category/filter change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentPage(1);
  }, [categorySlug]);

  // Handle page change in pagination mode
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Calculate how many pages we need to have loaded
  const pagesNeeded = page;
  const pagesLoaded = data?.pages.length || 0;
  

    // If navigating to a page we haven't loaded yet, fetch more
    // const requiredProducts = page * pageSize;
    // if (requiredProducts > totalLoadedProducts && hasNextPage && !isFetchingNextPage) {
    //   fetchNextPage();
    // }

      // Fetch additional pages if needed
  if (pagesNeeded > pagesLoaded && hasNextPage && !isFetchingNextPage) {
    const pagesToFetch = pagesNeeded - pagesLoaded;
    // Fetch pages sequentially to avoid race conditions
    (async () => {
      for (let i = 0; i < pagesToFetch; i++) {
        await fetchNextPage();
      }
        })().catch((error) => {
      console.error('Failed to fetch additional product pages:', error);
      toast.error('Failed to load all products. Please try again.');
    });
  }
  
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Sync inStock filter with URL param
  useEffect(() => {
    const inStockParam = searchParams.get('inStock');
    setFilterOptions((prev) => ({
      ...prev,
      inStock: inStockParam === 'true' ? true : undefined,
    }));
  }, [searchParams]);

  // helper to update filterOptions and sync with URL
  const updateFilter = (k: keyof ProductFilters, v: any) => {
    setFilterOptions((prev) => ({ ...prev, [k]: v }));
    setCurrentPage(1);
    
    // Update URL params (only for non-category filters)
    const newParams = new URLSearchParams(searchParams);
    
    if (k === 'inStock') {
      if (v === true) {
        newParams.set('inStock', 'true');
      } else {
        newParams.delete('inStock');
      }
    }
    
    setSearchParams(newParams, { replace: true });
  };

  // clear all filters
  const clearAll = () => {
    setFilterOptions({ minPrice: 0, maxPrice: 1000, inStock: undefined, tags: [] });
    setCurrentPage(1);
    
    // Navigate to base products page to clear category
    window.location.href = '/products';
  };

  // page meta info
  const pageInfo = useMemo(() => {
    if (searchParams.get('featured') === 'true') {
      return {
        title: 'Featured Products',
        description: 'Our hand-picked selection of the best products',
        icon: Sparkles,
        gradient: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
      };
    }

    if ((category as any)?.name) {
      return {
        title: (category as any).name,
        description:
          (category as any).description || `Browse our ${(category as any).name.toLowerCase()} collection`,
        icon: productTypeFilter === ProductType.BOOK ? BookOpen : Package,
        gradient: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
      };
    }

    if (productTypeFilter === ProductType.BOOK) {
      return {
        title: 'All Books',
        description: 'Explore our complete collection of books',
        icon: BookOpen,
        gradient: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
      };
    }

    if (productTypeFilter === ProductType.STATIONERY) {
      return {
        title: 'All Stationery',
        description: 'Discover quality stationery items',
        icon: Package,
        gradient: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
      };
    }

    return {
      title: 'All Products',
      description: 'Browse our complete catalog',
      icon: Package,
      gradient: `linear-gradient(135deg, ${IVORY} 0%, ${MUTED} 100%)`,
    };
  }, [category, searchParams, productTypeFilter]);

  const Icon = pageInfo.icon;

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'popular', label: 'Most Popular' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'price-asc', label: 'Price: Low to High' },
    { value: 'price-desc', label: 'Price: High to Low' },
  ];

  const currentSortLabel = sortOptions.find((opt) => opt.value === sortBy)?.label || 'Sort by';

  return (
    <>
      <Helmet>
        <title>{pageInfo.title} | Bookstore</title>
        <meta name="description" content={pageInfo.description} />
      </Helmet>

      <div  className="min-h-screen bg-background">
        <div className="container-custom py-6 sm:py-8 lg:py-12 px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8 lg:mb-10">
            <div className="flex items-start gap-4 sm:gap-6 mb-4">
              <div
                className={` flex  justify-center items-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-primary-300 via-primary-400 to-primary-dark shadow-md`}
              >
                <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>

              <div className="flex-1 min-w-0">
                <h1  className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-2 tracking-tight text-primary">
                  {pageInfo.title}
                </h1>
                <p style={{ color: 'rgba(45,45,45,0.65)' }} className="text-base sm:text-lg">
                  {pageInfo.description}
                </p>
              </div>
            </div>

            {!isLoading && products.length > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                <div
                 
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 rounded-xl border border-primary-light"
                >
                  <div className="w-2 h-2 rounded-full animate-pulse bg-primary-300" />
                  <span style={{ color: TEXT }} className="text-sm font-medium">
                    {paginationMode === 'pages'
                      ? `Showing ${startIdx + 1}-${Math.min(endIdx, totalLoadedProducts)} of ${totalLoadedProducts}${
                          hasNextPage ? '+' : ''
                        }`
                      : `${totalLoadedProducts}${hasNextPage ? '+' : ''} ${
                          totalLoadedProducts === 1 ? 'product' : 'products'
                        }`}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Controls bar */}
          <div

            className="backdrop-blur-sm rounded-2xl p-3 sm:p-4 mb-6 sm:mb-8 sticky top-20 z-40 border border-background-muted bg-white/80"
          >
            <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4">
              {/* Sort dropdown */}
              <div className="relative">
                <button
                  onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl transition-colors text-sm font-medium"
                  style={{ border: `1px solid ${BORDER}`, color: TEXT }}
                >
                  <span className="hidden sm:inline">Sort:</span>
                  <span style={{ color: TEXT }}>{currentSortLabel}</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${sortDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {sortDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setSortDropdownOpen(false)} />
                    <div
                      style={{ borderColor: BORDER }}
                      className="absolute left-0 top-full mt-2 w-56 bg-white rounded-xl border py-2 z-50"
                    >
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value as ProductSortBy);
                            setSortDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${
                            sortBy === option.value ? 'font-medium' : ''
                          }`}
                          style={{
                            color: sortBy === option.value ? PRIMARY_DARK : TEXT,
                            background: sortBy === option.value ? PRIMARY_TINT : 'transparent',
                          }}
                        >
                          <span>{option.label}</span>
                          {sortBy === option.value && <Check className="w-4 h-4" />}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* View mode + filters */}
              <div className="flex items-center gap-2">
                {/* View mode toggle */}
                <div style={{ border: `1px solid ${BORDER}` }} className="flex items-center gap-1 bg-white rounded-xl p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-all duration-200 ${viewMode === 'grid' ? 'font-medium' : 'text-gray-500'}`}
                    title="Grid view"
                    style={{
                      color: viewMode === 'grid' ? PRIMARY_DARK : 'rgba(45,45,45,0.55)',
                      background: viewMode === 'grid' ? PRIMARY_TINT : 'transparent',
                    }}
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-all duration-200 ${viewMode === 'list' ? 'font-medium' : 'text-gray-500'}`}
                    title="List view"
                    style={{
                      color: viewMode === 'list' ? PRIMARY_DARK : 'rgba(45,45,45,0.55)',
                      background: viewMode === 'list' ? PRIMARY_TINT : 'transparent',
                    }}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>

                {/* Mobile filters button */}
                <button
                  onClick={() => setMobileFiltersOpen(true)}
                  className="lg:hidden inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors"
                  style={{
                    border: `1px solid ${BORDER}`,
                    background: 'white',
                    color: TEXT,
                  }}
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  <span>Filters</span>
                </button>
              </div>
            </div>
          </div>

          {/* Main content */}
          <main>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
              {/* Desktop filters sidebar */}
              <aside className="hidden lg:block">
                <div className="sticky top-36">
                  <div style={{ background: 'white', borderColor: '' }} className="rounded-2xl border  overflow-hidden">
                    {/* Header */}
                    <div  className="flex items-center justify-between p-4 sm:p-5 border-b bg-background-muted" >
                      <h3  className="text-lg text-primary-800 font-semibold flex items-center gap-2">
                        <SlidersHorizontal className="w-5 h-5 text-primary-800" />
                        Filters
                      </h3>
                      {(category?.id || filterOptions.inStock) && (
                        <button
                          onClick={clearAll}
                          className="text-sm font-medium"
                          style={{ color: PRIMARY }}
                        >
                          Clear all
                        </button>
                      )}
                    </div>

                    <div className="p-4 sm:p-5 space-y-6">
                      {/* Categories */}
                      <div>
                        <h4 style={{ color: TEXT }} className="text-sm  font-semibold mb-3 flex items-center gap-2">
                          <div style={{ background: PRIMARY }} className="w-1 h-4 rounded-full" />
                          Categories
                        </h4>
                        <div className="max-h-[50vh] overflow-y-auto pr-1 custom-scrollbar">
                          <CategoryTreeFilter
                            selectedCategoryId={category?.id}
                            onSelect={() => {}} // Navigation is handled in CategoryTreeFilter
                          />
                        </div>
                      </div>

                      {/* Availability */}
                      <div className="pt-6 border-t" style={{ borderColor: BORDER }}>
                        <h4 style={{ color: TEXT }} className="text-sm font-semibold mb-3 flex items-center gap-2">
                          <div style={{ background: '#3A7D44' }} className="w-1 h-4 rounded-full" />
                          Availability
                        </h4>
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={!!filterOptions.inStock}
                              onChange={() => updateFilter('inStock', !filterOptions.inStock)}
                              className="w-5 h-5 rounded border-2"
                              style={{
                                borderColor: BORDER,
                                accentColor: PRIMARY,
                              }}
                            />
                          </div>
                          <span style={{ color: 'rgba(45,45,45,0.85)' }} className="text-sm">
                            In stock only
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </aside>

              {/* Products section */}
              <section className="lg:col-span-3">
                {isLoading ? (
                  <div className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6' : 'space-y-4'}>
                    {[...Array(9)].map((_, i) => (
                      <div
                        key={i}
                        className="h-72 rounded-2xl"
                        style={{
                          background:
                            'linear-gradient(90deg, rgba(0,0,0,0.02) 0%, rgba(255,255,255,0.6) 50%, rgba(0,0,0,0.02) 100%)',
                          backgroundSize: '200% 100%',
                          animation: 'shimmer 1.6s linear infinite',
                        }}
                      />
                    ))}
                  </div>
                ) : products.length === 0 ? (
                  <div style={{ background: 'white', borderColor: BORDER }} className="text-center py-16 rounded-2xl border">
                    <div style={{ background: MUTED }} className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Package className="w-8 h-8 sm:w-10 sm:h-10" />
                    </div>
                    <h3 style={{ color: TEXT }} className="text-xl sm:text-2xl font-semibold mb-2">
                      No products found
                    </h3>
                    <p style={{ color: 'rgba(45,45,45,0.65)' }} className="text-sm sm:text-base mb-6">
                      Try adjusting your filters or search criteria
                    </p>
                    <button
                      onClick={clearAll}
                      className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-medium transition-all"
                      style={{
                        background: PRIMARY,
                        color: 'white',
                        border: `1px solid ${PRIMARY_DARK}`,
                      }}
                    >
                      Clear all filters
                    </button>
                  </div>
                ) : (
                  <>

                    {
                      viewMode === 'grid' ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                          {products.map((p: any) => (
                            <ProductCard key={p.id} product={p} />
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {products.map((p: any) => (
                            <ProductCardHorizontal key={p.id} product={p} />
                          ))}
                        </div>
                      )
                    }

                  

                    {/* Pagination or Load More based on mode */}
                    {paginationMode === 'pages' ? (
                      <div className="mt-10 sm:mt-12">
                        <Pagination
                          currentPage={currentPage}
                          totalPages={displayTotalPages}
                          onPageChange={handlePageChange}
                          hasNextPage={hasNextPage}
                          isLoading={isFetchingNextPage}
                          className="bg-white rounded-2xl border"
                        />
                      </div>
                    ) : (
                      hasNextPage && (
                        <div className="text-center mt-10 sm:mt-12">
                          <button
                            onClick={() => fetchNextPage()}
                            disabled={isFetchingNextPage}
                            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-medium transition-all"
                            style={{
                              background: PRIMARY,
                              color: 'white',
                              border: `1px solid ${PRIMARY_DARK}`,
                              boxShadow: 'none',
                            }}
                          >
                            {isFetchingNextPage ? (
                              <>
                                <Loader2 className="animate-spin w-5 h-5" />
                                <span>Loading...</span>
                              </>
                            ) : (
                              <span>Load more products</span>
                            )}
                          </button>
                        </div>
                      )
                    )}
                  </>
                )}
              </section>
            </div>
          </main>
        </div>
      </div>

      {/* Mobile filters modal */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0"
            style={{ background: 'rgba(0,0,0,0.32)' }}
            onClick={() => setMobileFiltersOpen(false)}
          />

          {/* Drawer */}
          <div style={{ background: 'white', borderColor: BORDER }} className="absolute inset-y-0 right-0 w-full max-w-sm rounded-l-2xl">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div style={{ background: MUTED }} className="flex items-center justify-between p-5 border-b">
                <h2 style={{ color: TEXT }} className="text-lg font-semibold flex items-center gap-2">
                  <SlidersHorizontal className="w-5 h-5" />
                  Filters
                </h2>
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="p-2 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-5 space-y-6">
                {/* Categories */}
                <div>
                  <h3 style={{ color: TEXT }} className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <div style={{ background: PRIMARY }} className="w-1 h-4 rounded-full" />
                    Categories
                  </h3>
                  <CategoryTreeFilter
                    selectedCategoryId={category?.id}
                    onSelect={() => {
                      setMobileFiltersOpen(false);
                    }}
                  />
                </div>

                {/* Availability */}
                <div className="pt-6 border-t" style={{ borderColor: BORDER }}>
                  <h3 style={{ color: TEXT }} className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <div style={{ background: '#3A7D44' }} className="w-1 h-4 rounded-full" />
                    Availability
                  </h3>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!filterOptions.inStock}
                      onChange={() => updateFilter('inStock', !filterOptions.inStock)}
                      className="w-5 h-5 rounded border-2"
                      style={{
                        borderColor: BORDER,
                        accentColor: PRIMARY,
                      }}
                    />
                    <span style={{ color: 'rgba(45,45,45,0.85)' }} className="text-sm">In stock only</span>
                  </label>
                </div>
              </div>

              {/* Footer actions */}
              <div style={{ borderTop: `1px solid ${BORDER}` }} className="p-5 bg-white flex gap-3">
                <button
                  onClick={() => {
                    clearAll();
                    setMobileFiltersOpen(false);
                  }}
                  className="flex-1 py-3 rounded-xl font-medium transition-colors"
                  style={{
                    background: 'transparent',
                    border: `1px solid ${BORDER}`,
                    color: TEXT,
                  }}
                >
                  Clear
                </button>
                <button
                  onClick={() => {
                    setMobileFiltersOpen(false);
                    refetch();
                  }}
                  className="flex-1 py-3 rounded-xl font-medium transition-all"
                  style={{
                    background: PRIMARY,
                    color: 'white',
                    border: `1px solid ${PRIMARY_DARK}`,
                  }}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductsPage;
